import { Check, Plus } from "lucide-mui";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import List from "@mui/material/List";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { useDebouncedValue } from "../../../hooks/useDebouncedValue.ts";
import SearchField from "../../SearchField.tsx";
import type { FilterOption, OptionsSpec } from "../specs.ts";

interface OptionsEditorProps {
  spec: OptionsSpec;
  values: string[];
  onChange: (values: string[]) => void;
  /** A label learned from a search, so the pill can show it. */
  onLabel: (value: string, label: string) => void;
  /** Picked in a single-choice list. */
  onDone: () => void;
}

const SEARCH_THRESHOLD = 7;

// Results of a server-side option search, per loader, for the session.
const loadCache = new WeakMap<object, Map<string, FilterOption[]>>();

/** A checklist (or radio list) of a filter's options, searchable when long. */
export default function OptionsEditor({ spec, values, onChange, onLabel, onDone }: OptionsEditorProps) {
  const multiple = spec.multiple ?? true;
  const [query, setQuery] = useState("");
  const settledQuery = useDebouncedValue(query, 300);
  const [loaded, setLoaded] = useState<FilterOption[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [cursor, setCursor] = useState(0);
  const listRef = useRef<HTMLUListElement>(null);

  const { loadOptions } = spec;
  useEffect(() => {
    if (!loadOptions) return;
    let cache = loadCache.get(loadOptions);
    if (!cache) {
      cache = new Map();
      loadCache.set(loadOptions, cache);
    }
    const hit = cache.get(settledQuery);
    if (hit) {
      setLoaded(hit);
      return;
    }
    let cancelled = false;
    setLoading(true);
    loadOptions(settledQuery)
      .then((results) => {
        cache.set(settledQuery, results);
        if (!cancelled) setLoaded(results);
      })
      .catch(() => {
        if (!cancelled) setLoaded([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [loadOptions, settledQuery]);

  const all = useMemo(() => (loadOptions ? (loaded ?? []) : (spec.options ?? [])), [loadOptions, loaded, spec.options]);
  const searchable = !!loadOptions || !!spec.freeSolo || all.length > SEARCH_THRESHOLD;

  const q = query.trim().toLowerCase();
  const filtered = loadOptions || !q ? all : all.filter((o) => `${o.label} ${o.value}`.toLowerCase().includes(q));
  // Picked values a search result doesn't hold (typed keywords, older picks) stay listed on top.
  const known = new Set(all.map((o) => o.value));
  const extra: FilterOption[] = values
    .filter((v) => !known.has(v) && (!q || v.toLowerCase().includes(q)))
    .map((v) => ({ value: v, label: v }));
  // A single choice can always go back to "any" (e.g. switch off a default).
  const ANY = "\u0000any";
  const anyRow: FilterOption[] =
    !multiple && !spec.required && !q ? [{ value: ANY, label: spec.noneLabel ?? "Any" }] : [];
  const rows = [...anyRow, ...extra, ...filtered];
  const canAdd =
    !!spec.freeSolo && !!query.trim() && !rows.some((o) => o.label.toLowerCase() === q || o.value.toLowerCase() === q);

  const toggle = (option: FilterOption) => {
    if (option.value === ANY) {
      onChange([]);
      onDone();
      return;
    }
    onLabel(option.value, option.label);
    if (!multiple) {
      onChange(values.includes(option.value) && !spec.required ? [] : [option.value]);
      onDone();
      return;
    }
    onChange(values.includes(option.value) ? values.filter((v) => v !== option.value) : [...values, option.value]);
  };

  const addTyped = () => {
    const value = query.trim();
    if (!value) return;
    if (!values.some((v) => v.toLowerCase() === value.toLowerCase())) {
      onChange(multiple ? [...values, value] : [value]);
    }
    setQuery("");
    if (!multiple) onDone();
  };

  // With text that matches no option, "Add …" is the first row and Enter adds it.
  const offset = canAdd ? 1 : 0;
  const count = rows.length + offset;
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCursor((c) => Math.min(c + 1, count - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (canAdd && cursor === 0) addTyped();
      else if (rows[cursor - offset]) toggle(rows[cursor - offset]);
    }
  };

  useEffect(() => setCursor(0), [query]);
  // Without a search box, the list takes focus so arrows and Enter work.
  useEffect(() => {
    if (!searchable) listRef.current?.focus();
  }, [searchable]);
  useEffect(() => {
    listRef.current?.querySelector<HTMLElement>(`[data-row="${cursor}"]`)?.scrollIntoView({ block: "nearest" });
  }, [cursor]);

  return (
    <Box onKeyDown={onKeyDown}>
      {searchable && (
        <Box sx={{ px: 1, pt: 1, pb: 0.5 }}>
          <SearchField
            value={query}
            onChange={setQuery}
            placeholder={spec.searchPlaceholder ?? (spec.freeSolo ? "Search or type a keyword" : "Search")}
            autoFocus
            fullWidth
          />
        </Box>
      )}
      <List ref={listRef} tabIndex={-1} sx={{ maxHeight: 300, overflowY: "auto", outline: "none" }} role="listbox" aria-multiselectable={multiple}>
        {canAdd && (
          <MenuItem
            data-row={0}
            role="option"
            className={cursor === 0 ? "Mui-focused" : undefined}
            onClick={addTyped}
            onMouseMove={() => setCursor(0)}
            sx={{ color: "primary.main" }}
          >
            <ListItemIcon sx={{ color: "inherit" }}>
              <Plus />
            </ListItemIcon>
            <ListItemText primary={`Add “${query.trim()}”`} slotProps={{ primary: { noWrap: true } }} />
          </MenuItem>
        )}
        {rows.map((option, index) => {
          const i = index + offset;
          const selected = option.value === ANY ? values.length === 0 : values.includes(option.value);
          return (
            <MenuItem
              key={option.value}
              data-row={i}
              role="option"
              aria-selected={selected}
              className={i === cursor ? "Mui-focused" : undefined}
              onClick={() => toggle(option)}
              onMouseMove={() => setCursor(i)}
            >
              <ListItemIcon sx={{ minWidth: 16 }}>
                {multiple ? (
                  <Checkbox size="small" checked={selected} tabIndex={-1} disableRipple sx={{ p: 0 }} />
                ) : (
                  selected && <Check sx={{ color: "primary.main" }} />
                )}
              </ListItemIcon>
              {option.icon}
              <ListItemText
                primary={option.label}
                slotProps={{ primary: { noWrap: true, sx: { fontWeight: selected ? 600 : undefined } } }}
              />
              {option.hint != null && (
                <Typography variant="caption" sx={{ color: "text.secondary", flexShrink: 0 }}>
                  {option.hint}
                </Typography>
              )}
            </MenuItem>
          );
        })}
        {rows.length === 0 && !canAdd && (
          <Box sx={{ px: 1.5, py: 2, display: "flex", justifyContent: "center", color: "text.secondary" }}>
            {loading || spec.loading ? (
              <CircularProgress size={18} />
            ) : (
              <Typography variant="body2" sx={{ color: "inherit" }}>
                {loadOptions && !query ? "Type to search" : "No matches"}
              </Typography>
            )}
          </Box>
        )}
      </List>
    </Box>
  );
}
